import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
} from "@radix-ui/react-dropdown-menu";
import { Spinner } from "components";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DataTable } from "components/Table/DataTable";
import { getGenericOrderList, getProducts } from "service/apis/Opportunities";
import { GenericOrder } from "service/apis/Opportunities";
import { AxiosRequestConfig } from "axios";
import { ColumnDef } from "@tanstack/react-table";
import { forexOrderStatuses } from "common/filters";
import { useNavigate } from "react-router-dom";
import NavbarWithActions from "components/Navbar/navbarwithactions";


const GenericAllProduct = () => {
    const { state } = useLocation();
    type Product = {
        id: number;
        name: string;
        subCategory: string;
    };

    /** -------------------- State -------------------- */
    const [search, setSearch] = useState<string>(state?.search || "");
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});

    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<Record<string, Product[]>>({});
    const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const [orders, setOrders] = useState<OrderRow[]>([]);
    const navigate = useNavigate();

    /** -------------------- Debounce Search -------------------- */
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    /** -------------------- Fetch Orders -------------------- */
    const fetchOrders = async () => {
        try {
            setLoading(true);
            const payload: AxiosRequestConfig = {
                params: {
                    page: 1,
                    per_page: 2000,
                    q: debouncedSearch || "",
                    product_id: selectedProducts,
                    // status:[]
                },
            };

            const { data } = await getGenericOrderList(payload);

            const formattedData: OrderRow[] = data.items.map(
                (order: GenericOrder) => {
                  
                    const createdAt = new Date(order.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                    });

                    return {
                        id: `ORD-${order.id}`,
                        created_at: createdAt,
                        customer: order.product[0]?.customer?.full_name ?? "-",
                        products: order.product.map((p) => p.name).join(", "),
                        totalValue: order.total_payable || 0,
                        status: order.maxx_status || "Open",
                    };
                }
            );

            setOrders(formattedData);
        } catch (error) {
            console.error("Failed to fetch generic orders", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [debouncedSearch, selectedProducts]);
    useEffect(() => {
        if (orders.length >= 0) {
            const hash = "#Orders";
            window.location.hash = hash;
        }
}, [orders]);
    const fetchProducts = async () => {
        try {
            const { data } = await getProducts({
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            const grouped: Record<string, Product[]> = data.reduce(
                (acc: Record<string, Product[]>, product: any) => {
                    const key = product.subCategory?.trim() || "Others";

                    if (!acc[key]) acc[key] = [];

                    acc[key].push({
                        id: product.id,
                        name: product.name,
                        subCategory: key,
                    });

                    return acc;
                },
                {}
            );

            setProducts(grouped);
        } catch (error) {
            console.error("Failed to fetch products", error);
        }
    };

    const handleRowClick = (row: any) => {
        setSelectedRow(row);

        // Remove "ORD-" prefix
        const numericId = String(row.id).replace(/^ORD-/, "");

        navigate(`/genericOrder/${numericId}`);
    };


    useEffect(() => {
        fetchProducts();
    }, []);
    const toggleCategory = (category: string) => {
        setOpenCategories((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    const toggleProduct = (id: number) => {
        setSelectedProducts((prev) =>
            prev.includes(id)
                ? prev.filter((p) => p !== id)
                : [...prev, id]
        );
    };

    /** -------------------- Types -------------------- */
    type OrderRow = {
        created_at: number | string;
        id: string;
        customer: string;
        products: string;
        totalValue: number;
        status: string;
    };

    /** -------------------- Table Columns -------------------- */
    const columns: ColumnDef<OrderRow>[] = [
        {
            accessorKey: "id",
            header: "Order ID",
            cell: ({ row }) => (
                <span className="font-medium text-[#000000]">{row.original.id}</span>
            ),
        },
        {
            accessorKey: "createdAt",
            header: "Created at",
            cell: ({ row }) => (
                <span className="font-medium text-[#000000]">{row.original.created_at}</span>
            ),
        },
        {
            accessorKey: "customer",
            header: "Customer",
            cell: ({ row }) => (
                <span className="font-medium text-gray-900">{row.original.customer}</span>
            ),
        },
        {
            accessorKey: "products",
            header: "Products",
            cell: ({ row }) => <span className="text-gray-700">{row.original.products}</span>,
        },
        {
            accessorKey: "totalValue",
            header: "INR Amount",
            cell: ({ row }) => (
                <span className="py-[3px] text-sm leading-4 font-medium text-left rounded-md">
                    ₹{row.original.totalValue.toLocaleString("en-IN")}
                </span>
            ),
        },
        {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => {
                const statusValue = row.original.status || "Unknown";

                const formattedStatus =
                    statusValue.charAt(0).toUpperCase() +
                    statusValue.slice(1).toLowerCase();

                const status = forexOrderStatuses.find(
                    (s) => s.value.toLowerCase() === statusValue.toLowerCase()
                );

                const bgColor = status?.bgColor || "#E5E7EB";
                const textColor = status?.textColor || "#374151";

                return (
                    <span
                        style={{ backgroundColor: bgColor, color: textColor }}
                        className="py-[3px] text-sm leading-4 font-medium px-2 text-center rounded-md"
                    >
                        {formattedStatus}
                    </span>
                );
            },


        },
    ];

    /** -------------------- UI -------------------- */
    return (
        <>
            {loading && <Spinner />}
            <NavbarWithActions header="Orders" />

            <div className="flex mt-2 rounded-xl h-full bg-white-A700 flex-col gap-2">
                {/* Search and Buttons */}
                <div className="flex w-full justify-between sm:flex-col sm:items-start  gap-2 items-center px-4 pt-4">
                    <div className="flex sm:flex sm:flex-col sm:items-start gap-2 items-center">
                        {/* Search Input */}
                        <div className="relative flex w-[300px]  items-center">
                            <Input
                                placeholder="Search by ID or customer name"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="py-3 w-[300px] pl-10 pr-4 flex gap-3.5 rounded-[30px] border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            />

                            <Search className="absolute left-3 h-3.5 text-gray-400" />
                        </div>

                        {/* Filter Icon */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button>
                                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" > <path d="M10.9384 23.1516C10.4584 23.1516 9.98844 23.0316 9.54844 22.7916C8.66844 22.3016 8.13844 21.4116 8.13844 20.4116V15.1116C8.13844 14.6116 7.80844 13.8616 7.49844 13.4816L3.75844 9.52156C3.12844 8.89156 2.64844 7.81156 2.64844 7.00156V4.70156C2.64844 3.10156 3.85844 1.85156 5.39844 1.85156H18.5984C20.1184 1.85156 21.3484 3.08156 21.3484 4.60156V6.80156C21.3484 7.85156 20.7184 9.04156 20.1284 9.63156L15.7984 13.4616C15.3784 13.8116 15.0484 14.5816 15.0484 15.2016V19.5016C15.0484 20.3916 14.4884 21.4216 13.7884 21.8416L12.4084 22.7316C11.9584 23.0116 11.4484 23.1516 10.9384 23.1516ZM5.39844 3.35156C4.69844 3.35156 4.14844 3.94156 4.14844 4.70156V7.00156C4.14844 7.37156 4.44844 8.09156 4.82844 8.47156L8.63844 12.4816C9.14844 13.1116 9.64844 14.1616 9.64844 15.1016V20.4016C9.64844 21.0516 10.0984 21.3716 10.2884 21.4716C10.7084 21.7016 11.2184 21.7016 11.6084 21.4616L12.9984 20.5716C13.2784 20.4016 13.5584 19.8616 13.5584 19.5016V15.2016C13.5584 14.1316 14.0784 12.9516 14.8284 12.3216L19.1084 8.53156C19.4484 8.19156 19.8584 7.38156 19.8584 6.79156V4.60156C19.8584 3.91156 19.2984 3.35156 18.6084 3.35156H5.39844Z" fill="#165FE3" /> <path d="M6.00358 11.2473C5.86358 11.2473 5.73358 11.2073 5.60358 11.1373C5.25358 10.9173 5.14358 10.4473 5.36358 10.0973L10.2936 2.19733C10.5136 1.84733 10.9736 1.73733 11.3236 1.95733C11.6736 2.17733 11.7836 2.63733 11.5636 2.98733L6.63358 10.8873C6.49358 11.1173 6.25358 11.2473 6.00358 11.2473Z" fill="#165FE3" /> </svg>
                                </button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent
                                className="  w-72  max-h-[320px]  overflow-y-auto  rounded-lg  bg-white-A700  shadow-lg  p-3  z-[9999]"
                            >
                                <p className="text-sm  mb-2">Filter by Products</p>

                                {Object.entries(products).map(([subcategory, items]) => {
                                    const isOpen = openCategories[subcategory];

                                    return (
                                        <div key={subcategory} className="mb-2">
                                            {/* Accordion Header */}
                                            <button
                                                type="button"
                                                onClick={() => toggleCategory(subcategory)}
                                                className="  w-full   flex   justify-between   items-center   text-sm   font-semibold  text-gray-600     py-1 hover:text-blue-600   "                             >

                                                <span>{subcategory || "Others"}</span>

                                                <svg
                                                    className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""
                                                        }`}
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>

                                            {/* Accordion Body */}
                                            {isOpen && (
                                                <div className="mt-1 ml-2 flex flex-col gap-1">
                                                    {items.map((product) => (
                                                        <label
                                                            key={product.id}
                                                            className="flex items-center gap-2 text-sm cursor-pointer"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedProducts.includes(product.id)}
                                                                onChange={() => toggleProduct(product.id)}
                                                                className="rounded border-gray-300"
                                                            />
                                                            <span>{product.name}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Actions */}
                                <div className="flex justify-end gap-2 mt-3 pt-2 border-t">
                                    <button
                                        onClick={() => setSelectedProducts([])}
                                        className="text-xs px-3 py-1 border rounded-md"
                                    >
                                        Clear
                                    </button>
                                    <button
                                        onClick={() => setFilterOpen(false)}
                                        className="text-xs px-3 py-1 bg-blue-600 text-white-A700 rounded-md"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </DropdownMenuContent>

                        </DropdownMenu>
                    </div>

                    {/* Create Order Button */}
                    <Link to={"/generic-order-creation"}>
                        <button className="rounded-lg py-[11px] px-4 border bg-blue-700 text-white-A700 font-semibold text-sm w-[150px]">
                            Create Order
                        </button>
                    </Link>
                </div>

                {/* Table */}
                <div className="flex-1 px-3 overflow-auto">
                    <DataTable
                        columns={columns}
                        data={orders}
                        isToolbarVisible={false}
                        isPaginationVisible
                        highlightRow={!!selectedRow}
                        onRowClick={handleRowClick}
                        searchData={{
                            placeholder: "Search",
                            columns: ["id", "customer"],
                        }}
                        defaultSorting={[{ id: "id", desc: true }]}
                    />
                </div>
            </div>
        </>
    );
};

export default GenericAllProduct;
